// Renders pitch-deck and other user-authored text as Markdown (images, links, lists, emphasis).
import { clsx } from "clsx";
import type { ReactNode } from "react";
import Markdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";
import { normalizePitchText } from "../lib/normalizePitchText.js";

/** react-markdown's defaultUrlTransform blocks data: URIs; pitch decks embed inline charts. */
function allowPitchMediaUrls(url: string): string {
  if (/^data:image\/[a-z0-9+.-]+;base64,/i.test(url)) {
    return url;
  }
  return defaultUrlTransform(url);
}

const markdownComponents: Components = {
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  img: ({ src, alt }) => (
    <img
      src={src}
      alt={alt ?? ""}
      className="my-2 max-h-96 max-w-full rounded border border-slate-200 bg-white object-contain"
      loading="lazy"
    />
  ),
  a: ({ href, children }) => (
    <a className="text-brand hover:underline" href={href} target="_blank" rel="noreferrer">
      {children}
    </a>
  ),
  strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => (
    <ul className="mb-2 list-outside list-disc space-y-1.5 pl-5 last:mb-0 marker:text-slate-400">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-2 list-outside list-decimal space-y-1.5 pl-5 last:mb-0 marker:text-slate-400">{children}</ol>
  ),
  li: ({ children }) => <li className="pl-0.5 leading-relaxed">{children}</li>,
  h1: ({ children }) => <h3 className="mb-1 text-base font-semibold text-slate-900">{children}</h3>,
  h2: ({ children }) => <h4 className="mb-1 text-sm font-semibold text-slate-900">{children}</h4>,
  h3: ({ children }) => <h5 className="mb-1 text-sm font-semibold text-slate-800">{children}</h5>,
  blockquote: ({ children }) => (
    <blockquote className="mb-2 border-l-2 border-slate-300 pl-3 text-slate-600 last:mb-0">{children}</blockquote>
  ),
  code: ({ className, children }) =>
    className ? (
      <code className={clsx("block overflow-x-auto rounded bg-slate-100 p-2 text-xs", className)}>{children}</code>
    ) : (
      <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">{children}</code>
    ),
  pre: ({ children }) => <pre className="mb-2 overflow-x-auto last:mb-0">{children}</pre>,
  hr: () => <hr className="my-3 border-slate-200" />,
  table: ({ children }) => (
    <div className="mb-2 overflow-x-auto last:mb-0">
      <table className="min-w-full border-collapse text-left text-xs">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="border border-slate-200 bg-slate-50 px-2 py-1 font-semibold">{children}</th>,
  td: ({ children }) => <td className="border border-slate-200 px-2 py-1">{children}</td>,
};

export function RichText({
  children,
  className,
  empty = "—",
}: {
  children: string;
  className?: string;
  empty?: ReactNode;
}): JSX.Element {
  const text = normalizePitchText(children);
  if (!text) {
    return <span className={clsx("text-slate-400", className)}>{empty}</span>;
  }

  return (
    <div className={clsx("rich-text text-sm leading-relaxed text-slate-800", className)}>
      <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents} urlTransform={allowPitchMediaUrls}>
        {text}
      </Markdown>
    </div>
  );
}
